import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useCreateVotingRoundMutation, useGetAccountsQuery } from '@/store/api/votingApi';
import toast from 'react-hot-toast';
import UploadImage from '@/components/common/UploadImage';
import { Account } from '@/types';

interface CreateSessionDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  sessionId: number;
}

export default function CreateVotingRound({ open, setOpen, sessionId }: CreateSessionDialogProps) {
  const [createVotingRound, isLoading] = useCreateVotingRoundMutation();
  const [imageLoading, setImageLoading] = useState(false);
  const [form, setForm] = useState<{
    title: string;
    description: string;
    userIds: number[];
    image: string;
  }>({
    title: '',
    description: '',
    userIds: [0],
    image: '',
  });
  const [errors, setErrors] = useState({
    title: '',
    description: '',
    contestants: '',
    image: '',
  });

  const handleCreateSession = async () => {
    let newErrors = {
      title: '',
      description: '',
      contestants: '',
      image: '',
    };

    if (!form.image) {
      newErrors.image = 'Banner image is required';
    }

    if (!form.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!form.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!form.userIds.length || form.userIds.includes(0)) {
      newErrors.contestants = 'Please select contestants';
    }

    setErrors(newErrors);

    if (newErrors.title || newErrors.description || newErrors.contestants || newErrors.image) {
      return;
    }
    try {
      await createVotingRound({
        sessionId: sessionId,
        body: {
          title: form.title,
          description: form.description,
          userIds: form.userIds,
          image: form.image,
        },
      }).unwrap();
      setOpen(false);
      toast.dismiss();
      toast.success('Voting round created successfully !!');
      setForm({
        title: '',
        description: '',
        userIds: [0],
        image: '',
      });
    } catch (err) {
      console.error('Failed to create voting session', err);
    }
  };

  const { data } = useGetAccountsQuery(
    {
      skip: 0,
      take: 1000,
      accountType: 'CONTESTANT',
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const activeUsers: Account[] =
    data?.data?.filter((item: Account) => item.status === 'Active') || [];

  const addDropdown = () => {
    setForm((prev) => ({
      ...prev,
      userIds: [...prev.userIds, 0],
    }));
  };

  const removeDropdown = (index: number) => {
    setForm((prev) => ({
      ...prev,
      userIds: prev.userIds.filter((_, i) => i !== index),
    }));
  };

  const handleSelectChange = (index: number, value: number) => {
    setForm((prev) => {
      const updated = [...prev.userIds];
      updated[index] = value;

      return {
        ...prev,
        userIds: updated,
      };
    });
    if (errors.contestants) {
      setErrors((prev) => ({
        ...prev,
        contestants: '',
      }));
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div
        className="w-full max-w-[720px] bg-[#0b1220] border border-gray-800 rounded-xl shadow-xl 
      max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 sm:p-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-xl sm:text-2xl font-semibold text-white">Create Voting Round</h2>

              <p className="text-gray-400 text-sm mt-1">Create a new voting round.</p>
            </div>

            <button onClick={() => setOpen(false)} className="p-1 hover:bg-gray-800 rounded">
              <X className="text-gray-400 hover:text-white" />
            </button>
          </div>

          <div className="space-y-5">
            <div className="space-y-2">
              <UploadImage
                label="Banner"
                onChange={(url) => {
                  setForm((prev) => ({
                    ...prev,
                    image: url,
                  }));
                  setErrors((prev) => ({
                    ...prev,
                    image: '',
                  }));
                }}
                setIsImageUploading={setImageLoading}
              />
              {errors.image && <p className="text-red-500 text-xs">{errors.image}</p>}
            </div>
            <div>
              <label className="text-sm text-gray-300">Title *</label>

              <input
                value={form.title}
                onChange={(e) => {
                  setForm((f) => ({ ...f, title: e.target.value }));
                  if (errors.title) {
                    setErrors((prev) => ({ ...prev, title: '' }));
                  }
                }}
                className="w-full mt-2 bg-[#050b18] border border-gray-700 rounded-lg px-4 py-2 text-white outline-none focus:border-blue-500"
                placeholder="Enter session title"
              />
              {errors.title && <p className="text-red-500 text-xs">{errors.title}</p>}
            </div>

            <div>
              <label className="text-sm text-gray-300">Description</label>

              <textarea
                value={form.description}
                onChange={(e) => {
                  setForm((f) => ({ ...f, description: e.target.value }));

                  if (errors.description) {
                    setErrors((prev) => ({ ...prev, description: '' }));
                  }
                }}
                rows={3}
                className="w-full mt-2 bg-[#050b18] border border-gray-700 rounded-lg px-4 py-2 text-white outline-none focus:border-blue-500 resize-none"
                placeholder="Describe the purpose of this voting session."
              />
              {errors.description && <p className="text-red-500 text-xs">{errors.description}</p>}
            </div>

            <div className="space-y-3">
              <label className="text-sm text-gray-300">Select Contestants *</label>

              {form.userIds.map((selectedId, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <select
                    value={selectedId || ''}
                    onChange={(e) => handleSelectChange(index, Number(e.target.value))}
                    className="flex-1 bg-[#050b18] border border-gray-700 rounded-lg px-4 py-2 text-white outline-none focus:border-blue-500"
                  >
                    <option value="">Select contestant</option>

                    {activeUsers
                      ?.filter((c: any) => !form.userIds.includes(c.id) || c.id === selectedId)
                      .map((contestant: any) => (
                        <option key={contestant.id} value={contestant.id}>
                          {contestant.firstname} {contestant.lastname}
                        </option>
                      ))}
                  </select>

                  {/* Add button */}
                  {index == 0 && (
                    <button
                      type="button"
                      onClick={addDropdown}
                      className="px-3 py-2 bg-green-600 rounded text-white"
                    >
                      +
                    </button>
                  )}

                  {/* Remove button */}
                  {form.userIds.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeDropdown(index)}
                      className="px-3 py-2 bg-red-600 rounded text-white"
                    >
                      -
                    </button>
                  )}
                </div>
              ))}
              {errors.contestants && <p className="text-red-500 text-xs">{errors.contestants}</p>}
            </div>
            <p className="text-xs text-gray-500">
              By adding a round, participants can vote on the available options during this stage of
              the voting session. You can manage the round settings and options after creation.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
            <button
              onClick={() => setOpen(false)}
              className="w-full sm:w-auto px-4 py-2 border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-800"
            >
              Cancel
            </button>

            <button
              onClick={handleCreateSession}
              className="w-full sm:w-auto px-5 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-white"
            >
              {isLoading ? 'Create Round' : 'Create Round'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
